import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "../../App";
import { CreditCard, Check } from "lucide-react";

export default function OwnerBillingPage() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    axios
      .get(`${API}/owner/billing`)
      .then((res) => setBilling(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse bg-muted h-64 rounded-xl" />;

  const sub = billing?.subscription;
  const plan = billing?.plan;
  const planName = sub ? ((lang === "el" && plan?.name_el) || sub.plan_name) : "";
  const planFeatures =
    lang === "el" && Array.isArray(plan?.features_el) && plan.features_el.length
      ? plan.features_el
      : plan?.features || [];
  const statusColor = {
    active: "bg-green-100 text-green-700",
    paused: "bg-amber-100 text-amber-700",
    expired: "bg-red-100 text-red-700",
  }[sub?.status] || "bg-muted text-muted-foreground";

  return (
    <div data-testid="owner-billing-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("owner:billing_title")}</h1>

      <div className="bg-card rounded-xl border border-border p-6 max-w-2xl">
        {sub ? (
          <div>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("owner:billing_current_plan")}</p>
                  <p className="text-xl font-medium text-foreground">{planName}</p>
                  {sub.amount > 0 && (
                    <p className="text-sm text-muted-foreground">${sub.amount}/mo</p>
                  )}
                </div>
              </div>
              <span
                className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor}`}
                data-testid="owner-billing-status"
              >
                {t(`subscription_status.${sub.status}`, { defaultValue: sub.status })}
              </span>
            </div>

            {(sub.start_date || sub.end_date) && (
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-muted/40 rounded-lg">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("owner:billing_start_date")}</p>
                  <p className="text-sm font-medium" data-testid="owner-billing-start">
                    {sub.start_date || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("owner:billing_end_date")}</p>
                  <p className="text-sm font-medium" data-testid="owner-billing-end">
                    {sub.end_date || "—"}
                  </p>
                </div>
              </div>
            )}

            {plan?.features?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t("owner:billing_included")}</p>
                <ul className="space-y-1.5">
                  {planFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-6">
              {t("owner:billing_change_plan_help")}
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="font-medium text-foreground mb-1">{t("owner:billing_no_active_plan")}</p>
            <p className="text-sm text-muted-foreground">
              {t("owner:billing_no_active_plan_help")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
